import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { EntityRemoveDialogComponent } from '@frontend/shared/components/entity-remove-dialog.component';
import { OcrService } from '@frontend/shared/services/ocr.service';
import { PromanStateService } from '@frontend/shared/services/proman-state.service';
import { SharedComponentsModule } from '@frontend/shared-components/shared-components.module';
import { PromanDialogCoreModule } from '@proman/dialog/core/proman-dialog-core.module';
import { PromanDialogService } from '@proman/dialog/proman-dialog.service';
import { PromanFile } from '@proman/interfaces/entity-interfaces';
import { TableConfig } from '@proman/interfaces/object-interfaces';
import { Entity } from '@proman/services/entity.service';
import { FilePreviewService } from '@proman/services/file-preview.service';
import { FilterService } from '@proman/services/filter.service';
import { ToastService } from '@proman/services/toast.service';
import { TranslatePipe } from '@proman/shared/pipes/pipes/translate.pipe';
import { ConfirmActionDialogComponent } from '@proman/shared-dialogs/dialogs/confirm-action-dialog.component';
import { PromanTableModule } from '@proman/table';
import { sequentialRequests } from '@proman/utils';

@Component({
    standalone: true,
    selector: 'pm-ocr-files',
    template: `
        <pm-toolbar-driver [title]="'ocr_files' | translate"></pm-toolbar-driver>
        <pro-table [config]="tableConfig" [timeStamp]="timestamp"></pro-table>
    `,
    imports: [CommonModule, PromanDialogCoreModule, PromanTableModule, SharedComponentsModule, TranslatePipe],
})
export class OcrFilesComponent implements OnInit {
    private OCR = inject(OcrService);


    FilePreview = inject(FilePreviewService);
    Filter = inject(FilterService);

    tableConfig: TableConfig;
    timestamp: number;
    Entity = inject(Entity);
    Dialog = inject(PromanDialogService);
    PromanState = inject(PromanStateService);
    Toast = inject(ToastService);

    ngOnInit() {
        this.setTableConfig();
        this.refresh();
    }

    setTableConfig() {
        this.tableConfig = {
            entity: 'file',
            rowButtons: [
                {
                    icon: 'file',
                    tooltip: 'see_file',
                    acl: null,
                    callback: (row: PromanFile) => this.FilePreview.show(row),
                },
                {
                    icon: 'chevron-right',
                    tooltip: 'go_to_purchase',
                    acl: 'purchase.display',
                    callback: (row: PromanFile) => {
                        const notFoundCallback = () => {
                            this.Toast.pop('error', 'no_purchase_found_for_this_file');
                        };
                        this.Entity.get('purchase')
                            .get({ 'invoiceFile.id': row.id })
                            .then((purchase) => {
                                if (purchase) {
                                    this.PromanState.to('Purchase', purchase.id);
                                } else {
                                    notFoundCallback();
                                }
                            })
                            .catch((err) => {
                                notFoundCallback();
                            });
                    },
                },
                {
                    icon: 'trash',
                    tooltip: 'delete',
                    action: 'remove',
                    acl: null,
                    dialog2: {
                        component: EntityRemoveDialogComponent,
                        locals: { entity: 'file' },
                    },
                    onSuccess: this.refresh,
                },
            ],
            topButtons: [
                {
                    icon: 'print-magnifying-glass',
                    tooltip: 'invoice_analyze',
                    label: 'invoice_analyze',
                    acl: null,
                    callback: this.filesAnalyze,
                },
            ],
            actionButtons: [
                {
                    label: 'retry',
                    acl: null,
                    callback: (rows: PromanFile[]) => {
                        const files = rows.map((file) => file);
                        this.retryFilesAnalyze(files);
                    },
                },
                {
                    label: 'delete',
                    action: 'remove',
                    acl: null,
                    callback: (rows: PromanFile[]) => {
                        this.Dialog.open(ConfirmActionDialogComponent, { question: 'delete_selected_files' }).then(
                            () => {
                                const fileEntity = this.Entity.get('file');
                                const requests: Array<() => Promise<unknown>> = rows.map(
                                    (file) => () => fileEntity.remove({ id: file.id })
                                );

                                sequentialRequests(requests).then(() => this.refresh());
                            }
                        );
                    },
                },
            ],
            fields: [
                {
                    name: 'id',
                    key: 'id',
                },
                {
                    name: 'createdAt',
                    key: 'createdAt',
                    formatter: 'dateTime',
                    formatterConfig: '_datetime_js',
                    showTime: true,
                },
                {
                    name: 'name',
                    key: 'name',
                },
                {
                    name: 'extension',
                    key: 'extension',
                },
                {
                    name: 'comments',
                    key: 'comments',
                },
                {
                    name: 'status',
                    key: 'status',
                    getValue: this.getOcrStatus,
                    formatter: 'compile',
                    filter: {
                        type: 'dropdown_multi',
                        options: this.getDropdownFilterOptions(),
                    },
                },
            ],
            extraParameters: {
                ocr: true,
            },
        };
    }

    getOcrStatus = (file: PromanFile) => {
        const icons = {
            uploaded: { icon: 'upload', color: 'blue' },
            processing: { icon: 'spinner-scale', color: 'blue', animation: 'spin' },
            converted: { icon: 'file-import', color: 'green' },
            failed: { icon: 'ban', color: 'red' },
        };
        const icon = icons[file.status];

        return {
            icon,
            text: this.Filter.translate(file.status),
        };
    };

    getDropdownFilterOptions() {
        return ['uploaded', 'processing', 'converted', 'failed'].map((str) => ({
            id: str,
            name: this.Filter.translate(str),
        }));
    }

    refresh = () => (this.timestamp = new Date().getTime());

    filesAnalyze = () => this.OCR.filesAnalyze().then(() => this.refresh());
    retryFilesAnalyze = (files: PromanFile[]) => {
        this.OCR.retryFilesAnalyze(files).then(() => this.refresh());
    };
}
